import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PhotoUpload } from "@/components/PhotoUpload";
import { STAGE_META } from "@/lib/plant";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

const STAGES = Object.keys(STAGE_META);
const CUSTOM = "__custom__";
const empty = {
  name: "",
  species: "",
  variety: "",
  growth_stage: "Seed",
  planted_date: new Date().toISOString().slice(0, 10),
  location: "",
  watering_interval_days: 3,
  notes: "",
  photo_path: "",
};

export function PlantDialog({ open, onOpenChange, onSaved, plant }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [vegList, setVegList] = useState([]);
  const [customSpecies, setCustomSpecies] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const isEdit = Boolean(plant);

  useEffect(() => {
    if (open) {
      api.get("/guides").then(({ data }) => setVegList(data.vegetables.map((v) => v.name))).catch(() => setVegList([]));
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      const initial = plant
        ? {
            name: plant.name || "",
            species: plant.species || "",
            variety: plant.variety || "",
            growth_stage: plant.growth_stage || "Seed",
            planted_date: plant.planted_date || new Date().toISOString().slice(0, 10),
            location: plant.location || "",
            watering_interval_days: plant.watering_interval_days || 3,
            notes: plant.notes || "",
            photo_path: plant.photo_path || "",
          }
        : empty;
      setForm(initial);
      setCustomSpecies(false);
    }
  }, [open, plant]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const identify = async () => {
    if (!form.photo_path) return;
    if (!user?.is_premium) {
      toast.info("AI Identify is a Sprout Pro feature");
      onOpenChange(false);
      navigate("/pricing");
      return;
    }
    setIdentifying(true);
    try {
      const { data } = await api.post("/identify", { photo_path: form.photo_path });
      setForm((f) => ({
        ...f,
        species: data.common_name || data.species || f.species,
        name: f.name || data.common_name || data.species || "",
        notes: data.quick_care?.length ? `${f.notes ? f.notes + "\n\n" : ""}AI care tips:\n• ${data.quick_care.join("\n• ")}`.trim() : f.notes,
      }));
      if (data.common_name && !vegList.includes(data.common_name)) setCustomSpecies(true);
      toast.success(`Identified: ${data.common_name || data.species || "see notes"} (${data.confidence || "?"} confidence)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not identify");
    } finally {
      setIdentifying(false);
    }
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Please give your sprout a name");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, watering_interval_days: Number(form.watering_interval_days) || 3 };
      if (isEdit) {
        await api.put(`/plants/${plant.id}`, payload);
        toast.success("Sprout updated");
      } else {
        await api.post("/plants", payload);
        toast.success("Sprout added to your garden 🌱");
      }
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="plant-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? "Edit Sprout" : "Add a Sprout"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <PhotoUpload value={form.photo_path} onChange={(p) => set("photo_path", p)} testId="plant-photo" />
            {form.photo_path && (
              <Button
                type="button"
                variant="secondary"
                onClick={identify}
                disabled={identifying}
                className="w-full rounded-xl"
                data-testid="identify-button"
              >
                {identifying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5 text-emerald-600" />}
                Identify with AI
              </Button>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Basil on the windowsill"
              data-testid="plant-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Species</Label>
              {customSpecies || (form.species && !vegList.includes(form.species)) ? (
                <div className="flex gap-2">
                  <Input value={form.species} onChange={(e) => set("species", e.target.value)} placeholder="Type species" data-testid="plant-species-input" />
                  {vegList.length > 0 && (
                    <button type="button" onClick={() => { setCustomSpecies(false); set("species", ""); }} className="text-xs text-muted-foreground hover:text-foreground shrink-0 self-center" data-testid="species-pick-list">
                      List
                    </button>
                  )}
                </div>
              ) : (
                <Select
                  value={form.species || ""}
                  onValueChange={(v) => {
                    if (v === CUSTOM) { setCustomSpecies(true); set("species", ""); }
                    else set("species", v);
                  }}
                >
                  <SelectTrigger data-testid="plant-species-select">
                    <SelectValue placeholder="Choose a vegetable" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {vegList.map((v) => (
                      <SelectItem key={v} value={v} data-testid={`species-option-${v}`}>
                        {v}
                      </SelectItem>
                    ))}
                    <SelectItem value={CUSTOM} data-testid="species-option-custom">Other / custom…</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Variety</Label>
              <Input value={form.variety} onChange={(e) => set("variety", e.target.value)} placeholder="Genovese" data-testid="plant-variety-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Growth stage</Label>
              <Select value={form.growth_stage} onValueChange={(v) => set("growth_stage", v)}>
                <SelectTrigger data-testid="growth-stage-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`stage-option-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Planted date</Label>
              <Input type="date" value={form.planted_date} onChange={(e) => set("planted_date", e.target.value)} data-testid="plant-date-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Kitchen window" data-testid="plant-location-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Water every (days)</Label>
              <Input
                type="number"
                min={1}
                value={form.watering_interval_days}
                onChange={(e) => set("watering_interval_days", e.target.value)}
                data-testid="plant-interval-input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Care tips, soil, sunlight…" rows={3} data-testid="plant-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="plant-dialog-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800" data-testid="plant-dialog-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? "Save changes" : "Add sprout"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
